import React, { useEffect, useState } from "react";
import {
  CheckCircle2,
  Clock3,
  DollarSign,
  RotateCcw,
} from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

const cards = [
  {
    key: "held_minor",
    label: "Accrued — In Hold Period",
    icon: Clock3,
    iconWrap: "bg-blue-50 text-blue-600 ring-blue-100",
    helper: "Currently recorded within the 30-day hold workflow",
    emphasis: true,
  },
  {
    key: "approved_minor",
    label: "Approved for Payment",
    icon: CheckCircle2,
    iconWrap: "bg-emerald-50 text-emerald-600 ring-emerald-100",
    helper: "Approved for a commission payment cycle",
  },
  {
    key: "paid_ytd_minor",
    label: "Paid Year to Date",
    icon: DollarSign,
    iconWrap: "bg-violet-50 text-violet-600 ring-violet-100",
    helper: "Actual commission payments this calendar year",
  },
  {
    key: "adjustments_minor",
    label: "Adjustments",
    icon: RotateCcw,
    iconWrap: "bg-orange-50 text-orange-600 ring-orange-100",
    helper: "Refund / chargeback / correction activity",
  },
];

function money(minor, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format((Number(minor) || 0) / 100);
  } catch {
    return `${currency || ""} ${((Number(minor) || 0) / 100).toFixed(2)}`.trim();
  }
}

function getPaymentPresentation(requirements) {
  const payment = (requirements || []).find(
    (item) => item.key === "payment",
  );

  if (!payment) {
    return {
      tone: "slate",
      text: "Payment enrollment status is currently unavailable.",
      action: false,
    };
  }

  const state = payment.presentation_state || "NOT_CONFIGURED";

  if (payment.verified_for_payment || state === "VERIFIED") {
    return {
      tone: "emerald",
      text: "Payment enrollment verified. Approved commissions can proceed through the payment cycle.",
      action: false,
    };
  }

  if (state === "SUBMITTED_PENDING_VERIFICATION") {
    return {
      tone: "blue",
      text: "Payment details were submitted and are awaiting provider verification. Approved payments remain pending until verification is complete.",
      action: false,
    };
  }

  if (!payment.provider_configured || state === "NOT_CONFIGURED") {
    return {
      tone: "amber",
      text: "Commission payment enrollment is not yet available in this workspace.",
      action: false,
    };
  }

  if (state === "FAILED") {
    return {
      tone: "red",
      text: "Payment enrollment needs attention before approved commissions can be paid.",
      action: true,
    };
  }

  return {
    tone: "amber",
    text: "Complete payment enrollment to start receiving eligible commission payments.",
    action: true,
  };
}

function paymentToneClasses(tone) {
  if (tone === "emerald")
    return "border-emerald-200 bg-emerald-50 text-emerald-800";
  if (tone === "blue")
    return "border-blue-200 bg-blue-50 text-blue-800";
  if (tone === "red")
    return "border-red-200 bg-red-50 text-red-800";
  if (tone === "amber")
    return "border-amber-200 bg-amber-50 text-amber-900";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

export function CompactCommissionSnapshot({
  requirements = [],
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ledger, setLedger] = useState({
    summary_by_currency: [],
    entries: [],
  });

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setError("");

      const { data, error: loadError } = await supabase.rpc(
        "get_my_sales_affiliate_earnings",
      );

      if (!alive) return;

      if (loadError || !data?.ok) {
        setError(
          "Commission summary could not be loaded right now. Please try again.",
        );
        setLedger({
          summary_by_currency: [],
          entries: [],
        });
      } else {
        setLedger(data);
      }

      setLoading(false);
    })();

    return () => {
      alive = false;
    };
  }, []);

  const currencies = ledger.summary_by_currency || [];
  const payment = getPaymentPresentation(requirements);

  return (
    <section className="mt-7 rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-6 py-5 sm:px-7">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.24em] text-blue-600">
              Business &amp; Earnings Snapshot
            </div>

            <h2 className="mt-1 text-xl font-black text-slate-950">
              Your affiliate earnings at a glance
            </h2>
          </div>

          <Link
            to="/sales-affiliate/earnings"
            className="text-xs font-black text-blue-700 hover:text-blue-900"
          >
            View earnings &amp; payouts →
          </Link>
        </div>
      </div>

      {error ? (
        <div className="mx-6 mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800 sm:mx-7">
          {error}
        </div>
      ) : null}

      <div>
        {loading ? (
          <div className="px-6 py-8 text-sm font-semibold text-slate-500 sm:px-7">
            Loading commission summary…
          </div>
        ) : error ? null : currencies.length === 0 ? (
          <div className="mx-6 my-6 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-6 text-sm font-semibold text-slate-600 sm:mx-7">
            No qualifying commission activity has been recorded yet.
            Balances will appear by transaction currency when activity
            is recorded.
          </div>
        ) : (
          currencies.map((currencyRow, currencyIndex) => (
            <div
              key={currencyRow.currency}
              className={
                currencyIndex
                  ? "border-t border-slate-200"
                  : ""
              }
            >
              {currencies.length > 1 ? (
                <div className="px-6 pt-5 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 sm:px-7">
                  {currencyRow.currency} balances
                </div>
              ) : null}

              <div className="grid sm:grid-cols-2 xl:grid-cols-4">
                {cards.map((card, index) => {
                  const Icon = card.icon;

                  return (
                    <article
                      key={`${currencyRow.currency}-${card.label}`}
                      className={`p-6 ${
                        index
                          ? "border-t border-slate-100 sm:border-l sm:border-t-0"
                          : ""
                      } ${
                        card.emphasis
                          ? "ring-2 ring-inset ring-emerald-300"
                          : ""
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-1 ${card.iconWrap}`}
                        >
                          <Icon size={19} strokeWidth={2.2} />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-black uppercase leading-4 tracking-[0.16em] text-slate-500">
                            {card.label}
                          </div>

                          <div className="mt-2 text-3xl font-black text-slate-950">
                            {money(
                              currencyRow[card.key],
                              currencyRow.currency,
                            )}
                          </div>

                          <div className="mt-1 text-xs font-semibold text-slate-500">
                            {card.helper}
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      <div
        className={`mx-6 mb-6 rounded-2xl border px-4 py-3 text-sm font-semibold sm:mx-7 ${paymentToneClasses(
          payment.tone,
        )}`}
      >
        <div>{payment.text}</div>

        {payment.action ? (
          <Link
            to="/sales-affiliate/onboarding#payment"
            className="mt-2 inline-flex text-xs font-black underline underline-offset-2"
          >
            Go to Payment Setup →
          </Link>
        ) : null}
      </div>
    </section>
  );
}

export function ProgramTermsFootnote() {
  return (
    <section className="mt-7 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-700">
          Program Terms (Reference)
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-semibold text-slate-700">
          <span>
            Initial Sale: <strong>20%</strong>
          </span>

          <span>
            Monthly Renewal: <strong>10%</strong>
          </span>

          <span>
            <strong>30-day hold</strong>, then the 15th of the
            following month or the next Federal Reserve Bank business
            day
          </span>
        </div>

        <Link
          to="/sales-affiliate/agreement"
          className="text-xs font-black text-blue-700 hover:text-blue-900"
        >
          View Agreement →
        </Link>
      </div>

      <p className="mt-3 text-[11px] leading-5 text-slate-500">
        Payment-calendar calculations use U.S. Eastern Time.
        Future-rate changes apply prospectively and do not rewrite
        historical commission records.
      </p>
    </section>
  );
}
